import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  Quote,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";
import { Link } from "react-router-dom";

export default function ValeAPenaVoltar() {
  return (
    <GuideLayout
      title="Vale a pena voltar para o Brasil?"
      subtitle="A pergunta que todo dekassegui faz no domingo à noite. Não tem resposta única, mas tem matemática honesta."
      slug="vale-a-pena-voltar"
      description="Análise honesta de quando faz sentido voltar do Japão para o Brasil. Cálculo de poder de compra, custos reais, aspectos não-financeiros."
      updated="Maio de 2026"
      readTime="12 min"
    >
      <P>
        Você já fez essa conta na cabeça mil vezes. Olha o saldo, calcula o
        câmbio, sonha com a casa em São Paulo, no Paraná, em Goiás. E aí lembra
        do que deixou pra trás na última visita — o calor, os preços, o trânsito,
        a saudade ao contrário. Volta pra fábrica na segunda-feira sem decidir
        nada.
      </P>
      <P>
        Este guia não vai te dizer pra ficar ou pra voltar. Mas vai te dar a
        matemática honesta pra <Em>parar de adiar a decisão</Em>.
      </P>

      <H2 id="poder-de-compra">A primeira conta: poder de compra</H2>
      <P>
        Quem está no Japão olha pro saldo em ienes e converte mentalmente para
        reais. <Strong>Isso é metade da história.</Strong> O que importa não é
        quantos reais você tem, mas o que esses reais compram no lugar onde você
        vai morar.
      </P>
      <P>
        Um almoço executivo em Maizuru custa ¥800. Em São Paulo, custa R$ 35.
        Convertendo: ¥800 ≈ R$ 31. Parece que dá igual, mas tem nuance:
      </P>
      <UL>
        <li>
          Em Tóquio, esse mesmo almoço pode custar ¥1.500.
        </li>
        <li>
          Em uma cidade pequena do interior do Brasil, R$ 20.
        </li>
        <li>
          Em Niterói (Rio), R$ 45.
        </li>
      </UL>
      <P>
        Por isso a regra <Em>&ldquo;multiplica por 30 e tá feito&rdquo;</Em>{" "}
        é furada. O multiplicador certo depende de onde no Japão você ganha e
        onde no Brasil você vai gastar.
      </P>

      <Callout>
        <Strong>Atalho prático:</Strong>{" "}
        <Link to="/calculadoras/salario" className="text-accent underline">
          a calculadora de salário
        </Link>{" "}
        já mostra o equivalente em São Paulo do seu líquido. Use ela como ponto
        de partida e ajuste mentalmente para sua cidade-alvo no Brasil.
      </Callout>

      <H2 id="quanto-precisa">Quanto você precisa pra voltar com segurança</H2>
      <P>
        Há três cenários comuns. Cada um exige uma reserva diferente:
      </P>

      <H3>1. Voltar sem trabalho garantido</H3>
      <P>
        Você volta sem emprego marcado e vai procurar. <Strong>Mínimo
        recomendado:</Strong> 18 a 24 meses de despesas mensais previstas no
        Brasil. Por quê? Porque o mercado de trabalho brasileiro leva tempo, os
        salários iniciais costumam ser baixos, e qualquer urgência (saúde de
        familiar, casa que precisa de reforma) consome reserva rápido.
      </P>

      <H3>2. Voltar com emprego marcado</H3>
      <P>
        Reduz pra <Strong>6 a 12 meses</Strong> de reserva. Mas não menos.
        Empregos começam e terminam, e o salário inicial sempre demora a entrar.
      </P>

      <H3>3. Voltar para empreender</H3>
      <P>
        Aqui é o cenário mais delicado. <Strong>24 meses de reserva pessoal{" "}
        <Em>mais</Em> capital de giro do negócio para 12 meses operando no
        vermelho.</Strong> A maior causa de fracasso de dekassegui que volta pra
        montar negócio é subestimar quanto tempo o negócio leva pra dar lucro.
      </P>

      <Callout kind="warn">
        Atenção: muito brasileiro que volta usa a reserva como capital, fica sem
        nenhuma rede de proteção, e quando o negócio demora a engrenar, fica
        encurralado. Não misture reserva pessoal com capital de negócio.
      </Callout>

      <H2 id="custos-ocultos">Os custos que ninguém calcula</H2>
      <P>
        Quando você roda os números, costuma esquecer:
      </P>
      <UL>
        <li>
          <Strong>Mudança internacional:</Strong> entre ¥300.000 e ¥1.500.000
          dependendo do volume. Container ou aérea. Considere se vale a pena vender
          tudo aqui e comprar tudo lá — frequentemente vale.
        </li>
        <li>
          <Strong>Imposto de importação na bagagem desacompanhada:</Strong> a
          Receita Federal cobra sobre o que excede a cota livre. Eletrônicos
          novos são particularmente tributados.
        </li>
        <li>
          <Strong>Carro no Brasil:</Strong> a maioria precisa comprar carro no
          primeiro mês. Reserve no mínimo R$ 50.000 pra um seminovo decente.
        </li>
        <li>
          <Strong>Reforma da casa:</Strong> a casa que você imagina não vai estar
          como você lembra. Reserve algo para reparos básicos.
        </li>
        <li>
          <Strong>Plano de saúde particular:</Strong> R$ 600 a R$ 2.500 por
          pessoa por mês dependendo da idade e cobertura. Pra família, calcule
          esse gasto recorrente.
        </li>
        <li>
          <Strong>Educação dos filhos:</Strong> se você quer escola particular,
          R$ 1.500 a R$ 5.000 por filho por mês.
        </li>
        <li>
          <Strong>Aposentadoria:</Strong> se você pegar o dattai ichijikin, perde
          os anos de contribuição japonesa. Veja{" "}
          <Link to="/calculadoras/aposentadoria" className="text-accent underline">
            a calculadora de aposentadoria
          </Link>{" "}
          antes de decidir.
        </li>
      </UL>

      <H2 id="cambio">O fator câmbio é o maior risco</H2>
      <P>
        Você tem suas economias em ienes. O câmbio JPY/BRL oscila muito. Em 2020
        valia R$ 0,05. Em 2024 chegou a R$ 0,04. Em alguns momentos abaixo de
        R$ 0,03.
      </P>
      <P>
        <Strong>Cenário real:</Strong> se você tem ¥10 milhões guardados e o
        câmbio vai de R$ 0,04 para R$ 0,03 entre você decidir e efetivamente
        transferir, você &ldquo;perdeu&rdquo; R$ 100.000 sem fazer nada.
      </P>
      <P>
        Estratégias para mitigar:
      </P>
      <OL>
        <li>
          <Strong>Não converta tudo de uma vez.</Strong> Faça remessas mensais
          mesmo antes de voltar, formando uma reserva em reais.
        </li>
        <li>
          <Strong>Converta parte em USD</Strong> via Wise multi-currency e leve
          conta em dólar pra negociar quando o câmbio for favorável.
        </li>
        <li>
          <Strong>Tenha uma janela de decisão flexível.</Strong> Se você anuncia
          a volta para 6 meses específicos, fica refém do câmbio daquela janela.
        </li>
      </OL>

      <H2 id="parte-emocional">A parte que não cabe na planilha</H2>
      <P>
        Tem perguntas que não têm número:
      </P>
      <UL>
        <li>
          Você consegue dormir tranquilo com a segurança do Brasil?
        </li>
        <li>
          Você aguenta o ritmo brasileiro (informalidade, burocracia, atrasos)
          depois de anos no Japão?
        </li>
        <li>
          Seus filhos vão se adaptar à escola brasileira?
        </li>
        <li>
          Sua família no Brasil ainda é sua rede de suporte, ou virou família
          que você visita?
        </li>
        <li>
          Você se vê envelhecendo no Brasil — ou no Japão?
        </li>
      </UL>
      <P>
        Não tem resposta certa. Mas se você não fez essas perguntas em voz alta
        com quem mora com você, ainda não chegou na decisão real.
      </P>

      <Quote source="O que todo dekassegui que voltou já disse">
        Eu voltei porque achei que era hora. Não porque a planilha mandou.
      </Quote>

      <H2 id="testar-antes">Como testar a decisão antes de assumir</H2>
      <OL>
        <li>
          <Strong>Vá ao Brasil de férias por 30+ dias</Strong> em uma época não
          natalina. O Natal é todo mundo feliz, todo mundo recebendo. Janeiro,
          agosto, qualquer mês comum mostra a vida real.
        </li>
        <li>
          <Strong>Tente fazer as coisas chatas:</Strong> ir num banco, num
          cartório, tirar nota fiscal, ir num pronto-socorro. Se ainda assim
          você ama, é sinal real.
        </li>
        <li>
          <Strong>Converse com quem voltou recentemente.</Strong> Não com quem
          já está há 10 anos no Brasil. Com quem voltou ano passado. Eles têm a
          comparação fresca.
        </li>
        <li>
          <Strong>Faça uma simulação de 6 meses no Brasil</Strong> usando suas
          economias atuais. Se você consegue se imaginar com aquela vida — não
          a vida turística, mas a vida diária — você tem sua resposta.
        </li>
      </OL>

      <H2 id="conclusao">Conclusão honesta</H2>
      <P>
        Voltar pro Brasil pode ser a melhor decisão da sua vida. Pode ser a
        pior. A diferença não tá no Brasil ou no Japão — tá em quanto você se
        preparou.
      </P>
      <P>
        <Strong>Quem volta bem preparado:</Strong> tem reserva calculada,
        emprego ou negócio com plano realista, conversa difícil já feita com a
        família, expectativa ajustada. Geralmente reconstrói a vida em 1–3 anos.
      </P>
      <P>
        <Strong>Quem volta no impulso:</Strong> volta porque cansou, porque
        sentiu saudade num momento ruim, porque achou que ia ser fácil.
        Geralmente está procurando passaporte de volta em 6 meses.
      </P>
      <P>
        Se este guia te ajudou a fazer perguntas melhores, ele serviu. A
        resposta é sua. <Em>Honestidade antes de otimismo.</Em>
      </P>
    </GuideLayout>
  );
}
